import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Building2, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  Plus,
  Download,
  ArrowLeft,
  RefreshCw,
  Briefcase,
  Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { differenceInDays, isPast } from "date-fns";
import DocumentChecklistCard, { type DocumentStatus } from "@/components/shared/DocumentChecklistCard";
import { UnifiedDocumentLinkDialog, type DocConfig } from "@/components/shared/UnifiedDocumentLinkDialog";
import { SEO } from "@/components/seo/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import { useCompanies, type Company, type CompanyInput, LEGAL_FORM_LABELS } from "@/hooks/useCompanies";
import { CompanySelector } from "@/components/company/CompanySelector";
import { CompanyDialog } from "@/components/company/CompanyDialog";
import {
  LEGAL_DOCS_CONFIG,
  LEGAL_CATEGORY_ORDER,
  getEssentialLegalDocuments,
  type LegalDocumentConfig,
} from "@/config/legalDocs";
import type { Tables } from "@/integrations/supabase/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Types for legal documents
interface LegalDocument {
  id: string;
  document_type: string;
  document_id: string | null;
  company_id: string | null;
  label: string | null;
  valid_from: string | null;
  expires_at: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  document?: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
  } | null;
}

const LegalDocuments = () => {
  const navigate = useNavigate();
  const { user: _user } = useAuth();
  const { openDocument } = useDocumentPreview();
  
  // Companies management
  const { companies, isLoading: companiesLoading, createCompany, updateCompany, deleteCompany } = useCompanies();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [deletingCompany, setDeletingCompany] = useState<Company | null>(null);

  // Legal documents
  const [legalDocs, setLegalDocs] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocConfig, setSelectedDocConfig] = useState<DocConfig | null>(null);

  // Accordion state - open categories with issues by default
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  // Select first company when loaded
  useEffect(() => {
    if (companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  // Fetch legal documents for selected company
  useEffect(() => {
    if (selectedCompanyId) {
      fetchLegalDocuments();
    } else {
      setLegalDocs([]);
      setLoading(false);
    }
  }, [selectedCompanyId]);

  const fetchLegalDocuments = async () => {
    if (!selectedCompanyId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("legal_documents")
        .select(`
          *,
          document:documents(id, title, file_name, file_path)
        `)
        .eq("company_id", selectedCompanyId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setLegalDocs(data || []);
    } catch (error) {
      console.error("Error fetching legal documents:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les documents légaux",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getDocumentByType = (type: string) => {
    return legalDocs.find(doc => doc.document_type === type);
  };

  const getStatusInfo = (doc: LegalDocument | undefined, config: LegalDocumentConfig): { status: DocumentStatus; label: string } => {
    if (!doc || !doc.document) {
      return { status: "missing", label: "Non renseigné" };
    }

    if (!doc.expires_at) {
      return { status: "valid", label: "Valide" };
    }

    const expiresAt = new Date(doc.expires_at);
    const daysUntilExpiry = differenceInDays(expiresAt, new Date());

    if (isPast(expiresAt)) {
      return { status: "expired", label: `Expiré depuis ${Math.abs(daysUntilExpiry)} jours` };
    }

    // Special warning for short validity docs (Kbis)
    const warningDays = config.expiresShort ? 30 : 30;
    if (daysUntilExpiry <= warningDays) {
      return { status: "expiring", label: `Expire dans ${daysUntilExpiry} jours` };
    }

    return { status: "valid", label: "Valide" };
  };

  // Calculate stats for essential documents only
  const { stats, essentialProgress, categoriesWithIssues } = useMemo(() => {
    const essentialDocs = getEssentialLegalDocuments();
    const result = {
      total: essentialDocs.length,
      valid: 0,
      expiringSoon: 0,
      expired: 0,
      missing: 0,
    };

    const issueCategories: string[] = [];

    LEGAL_CATEGORY_ORDER.forEach(categoryKey => {
      const category = LEGAL_DOCS_CONFIG[categoryKey];
      let categoryHasIssue = false;

      category.documents.forEach(docConfig => {
        if (!docConfig.isEssential) return;
        
        const doc = getDocumentByType(docConfig.key);
        const statusInfo = getStatusInfo(doc, docConfig);
        
        if (statusInfo.status === "valid") result.valid++;
        else if (statusInfo.status === "expiring") {
          result.expiringSoon++;
          categoryHasIssue = true;
        }
        else if (statusInfo.status === "expired") {
          result.expired++;
          categoryHasIssue = true;
        }
        else {
          result.missing++;
          categoryHasIssue = true;
        }
      });

      if (categoryHasIssue && !issueCategories.includes(categoryKey)) {
        issueCategories.push(categoryKey);
      }
    });

    const progress = result.total > 0 ? Math.round((result.valid / result.total) * 100) : 0;

    return { 
      stats: result, 
      essentialProgress: progress,
      categoriesWithIssues: issueCategories 
    };
  }, [legalDocs]);

  // Open categories with issues by default
  useEffect(() => {
    if (categoriesWithIssues.length > 0 && openCategories.length === 0) {
      setOpenCategories(categoriesWithIssues);
    }
  }, [categoriesWithIssues]);

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  const handleUploadClick = (docConfig: LegalDocumentConfig) => {
    setSelectedDocConfig({
      key: docConfig.key,
      label: docConfig.label,
      doc_type: docConfig.doc_type,
      validityDays: docConfig.validityDays,
      keywords: docConfig.keywords,
    });
    setUploadDialogOpen(true);
  };

  const handleViewDocument = async (documentId: string) => {
    const { data: doc } = await supabase
      .from("documents")
      .select("*")
      .eq("id", documentId)
      .single();
    if (doc) openDocument(doc as Tables<"documents">);
  };

  const handleGeneratePack = async () => {
    toast({
      title: "Pack légal",
      description: "Génération du pack légal en cours...",
    });
  };

  // Company actions
  const handleAddCompany = () => {
    setEditingCompany(null);
    setCompanyDialogOpen(true);
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setCompanyDialogOpen(true);
  };

  const handleDeleteCompany = (company: Company) => {
    setDeletingCompany(company);
  };

  const handleCompanySubmit = async (data: CompanyInput) => {
    if (editingCompany) {
      await updateCompany.mutateAsync({ id: editingCompany.id, ...data });
    } else {
      const newCompany = await createCompany.mutateAsync(data);
      if (newCompany) {
        setSelectedCompanyId(newCompany.id);
      }
    }
  };

  const confirmDeleteCompany = async () => {
    if (deletingCompany) {
      await deleteCompany.mutateAsync(deletingCompany.id);
      if (selectedCompanyId === deletingCompany.id) {
        setSelectedCompanyId(companies.length > 1 ? companies.find(c => c.id !== deletingCompany.id)?.id || null : null);
      }
      setDeletingCompany(null);
    }
  };

  // Get category status summary
  const getCategoryStatus = (categoryKey: string) => {
    const category = LEGAL_DOCS_CONFIG[categoryKey];
    let valid = 0;
    let total = 0;
    let hasIssue = false;

    category.documents.forEach(docConfig => {
      if (docConfig.isEssential) {
        total++;
        const doc = getDocumentByType(docConfig.key);
        const statusInfo = getStatusInfo(doc, docConfig);
        if (statusInfo.status === "valid") valid++;
        else hasIssue = true;
      }
    });

    return { valid, total, hasIssue };
  };

  if (companiesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Documents Légaux Pro | EasyFinder"
        description="Gérez vos documents légaux d'entreprise : Kbis, URSSAF, attestations fiscales et plus encore."
      />

      <div className="min-h-screen bg-background md:pl-16">
        <div className="container max-w-6xl mx-auto px-4 py-6 pb-24 md:pb-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/documents")}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Documents Légaux Pro</h1>
              <p className="text-muted-foreground text-sm">
                Gérez les documents officiels de vos entreprises
              </p>
            </div>
            {selectedCompany && (
              <Button onClick={handleGeneratePack} variant="outline" className="hidden sm:flex">
                <Download className="w-4 h-4 mr-2" />
                Pack légal
              </Button>
            )}
          </div>

          {/* Company Selector */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-sm font-medium text-muted-foreground">Mes entreprises</h2>
            </div>
            <CompanySelector
              companies={companies}
              selectedCompanyId={selectedCompanyId}
              onSelect={setSelectedCompanyId}
              onAdd={handleAddCompany}
              onEdit={handleEditCompany}
              onDelete={handleDeleteCompany}
              isLoading={companiesLoading}
            />
          </div>

          {/* No company state */}
          {companies.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">Aucune entreprise</h3>
                <p className="text-muted-foreground mb-4">
                  Ajoutez votre première entreprise pour gérer ses documents légaux
                </p>
                <Button onClick={handleAddCompany}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une entreprise
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Content for selected company */}
          {selectedCompany && (
            <>
              {/* Company header */}
              <div className="flex items-center gap-3 mb-6 p-4 bg-muted/50 rounded-lg">
                <div className="p-2 rounded-full bg-primary/10">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold">{selectedCompany.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    {LEGAL_FORM_LABELS[selectedCompany.legal_form]}
                    {selectedCompany.siret && ` • SIRET: ${selectedCompany.siret}`}
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Progress Card */}
                  <Card className="mb-8">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            <Star className="w-4 h-4 text-amber-500" />
                            Documents essentiels
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {stats.valid} sur {stats.total} documents à jour
                          </p>
                        </div>
                        <div className={cn(
                          "text-3xl font-bold",
                          essentialProgress >= 80 ? "text-emerald-600" :
                          essentialProgress >= 50 ? "text-amber-600" : "text-destructive"
                        )}>
                          {essentialProgress}%
                        </div>
                      </div>
                      <Progress 
                        value={essentialProgress} 
                        className={cn(
                          "h-3",
                          essentialProgress >= 80 ? "[&>div]:bg-emerald-500" :
                          essentialProgress >= 50 ? "[&>div]:bg-amber-500" : "[&>div]:bg-destructive"
                        )}
                      />
                      
                      {/* Quick stats */}
                      <div className="grid grid-cols-4 gap-2 mt-4">
                        <div className="text-center p-2 rounded bg-emerald-500/10">
                          <p className="text-lg font-bold text-emerald-600">{stats.valid}</p>
                          <p className="text-xs text-muted-foreground">À jour</p>
                        </div>
                        <div className="text-center p-2 rounded bg-amber-500/10">
                          <p className="text-lg font-bold text-amber-600">{stats.expiringSoon}</p>
                          <p className="text-xs text-muted-foreground">Expire bientôt</p>
                        </div>
                        <div className="text-center p-2 rounded bg-destructive/10">
                          <p className="text-lg font-bold text-destructive">{stats.expired}</p>
                          <p className="text-xs text-muted-foreground">Expiré</p>
                        </div>
                        <div className="text-center p-2 rounded bg-muted">
                          <p className="text-lg font-bold">{stats.missing}</p>
                          <p className="text-xs text-muted-foreground">Manquant</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Categories Accordion */}
                  <Accordion 
                    type="multiple" 
                    value={openCategories}
                    onValueChange={setOpenCategories}
                    className="space-y-4"
                  >
                    {LEGAL_CATEGORY_ORDER.map(categoryKey => {
                      const category = LEGAL_DOCS_CONFIG[categoryKey];
                      const CategoryIcon = category.icon;
                      const { valid, total, hasIssue } = getCategoryStatus(categoryKey);

                      return (
                        <AccordionItem 
                          key={categoryKey} 
                          value={categoryKey}
                          className="border rounded-lg px-4 bg-card"
                        >
                          <AccordionTrigger className="hover:no-underline py-4">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={cn(
                                "p-2 rounded-lg",
                                hasIssue ? "bg-amber-500/10" : "bg-primary/10"
                              )}>
                                <CategoryIcon className={cn(
                                  "w-5 h-5",
                                  hasIssue ? "text-amber-600" : "text-primary"
                                )} />
                              </div>
                              <div className="text-left">
                                <p className="font-medium">{category.label}</p>
                                <p className="text-xs text-muted-foreground">
                                  {category.documents.length} documents
                                  {total > 0 && ` • ${valid}/${total} essentiels à jour`}
                                </p>
                              </div>
                              {hasIssue && (
                                <AlertTriangle className="w-4 h-4 text-amber-500 ml-auto mr-2" />
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pb-4">
                            <div className="grid gap-3 md:grid-cols-2">
                              {category.documents.map(docConfig => {
                                const doc = getDocumentByType(docConfig.key);
                                const statusInfo = getStatusInfo(doc, docConfig);
                                
                                // Calculate age warning for short validity docs
                                let ageWarning: { months: number } | undefined;
                                if (docConfig.expiresShort && doc?.document && doc.valid_from) {
                                  const validFrom = new Date(doc.valid_from);
                                  const now = new Date();
                                  const monthsDiff = (now.getFullYear() - validFrom.getFullYear()) * 12 + 
                                    (now.getMonth() - validFrom.getMonth());
                                  if (monthsDiff >= 2) {
                                    ageWarning = { months: monthsDiff };
                                  }
                                }
                                
                                return (
                                  <DocumentChecklistCard
                                    key={docConfig.key}
                                    typeKey={docConfig.key}
                                    label={docConfig.label}
                                    description={docConfig.description}
                                    icon={category.icon}
                                    required={docConfig.isEssential}
                                    status={statusInfo.status}
                                    statusLabel={statusInfo.label}
                                    isEssential={docConfig.isEssential}
                                    ageWarning={ageWarning}
                                    linkedDocument={doc?.document ? {
                                      id: doc.document.id,
                                      title: doc.document.title,
                                      file_name: doc.document.file_name,
                                      file_path: doc.document.file_path,
                                    } : null}
                                    expiresAt={doc?.expires_at}
                                    onLink={() => handleUploadClick(docConfig)}
                                    onView={handleViewDocument}
                                    onRefresh={fetchLegalDocuments}
                                    deleteTable="legal_documents"
                                    linkRecordId={doc?.id}
                                  />
                                );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Company Dialog */}
      <CompanyDialog
        open={companyDialogOpen}
        onOpenChange={(open) => {
          setCompanyDialogOpen(open);
          if (!open) setEditingCompany(null);
        }}
        company={editingCompany}
        onSubmit={handleCompanySubmit}
        isLoading={createCompany.isPending || updateCompany.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingCompany} onOpenChange={() => setDeletingCompany(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'entreprise ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera également tous les documents légaux associés à {deletingCompany?.name}. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCompany}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Document upload dialog */}
      {selectedDocConfig && selectedCompanyId && (
        <UnifiedDocumentLinkDialog
          open={uploadDialogOpen}
          onOpenChange={(open: boolean) => {
            setUploadDialogOpen(open);
            if (!open) setSelectedDocConfig(null);
          }}
          entityType="legal"
          entityId={selectedCompanyId}
          docConfig={selectedDocConfig}
          linkTableName="legal_documents"
          onSuccess={fetchLegalDocuments}
          showValidityDates={!!selectedDocConfig.validityDays}
        />
      )}
    </>
  );
};

export default LegalDocuments;
